/**
 * Протокол Следов и пороги GPS (docs/05-traces.md §10).
 *
 * Назначение файла: единственное место чисел античита и классификации качества Следа.
 */

import type { TraceQuality } from "./types.js";

/** Пороги GPS и жизни Следа (§10, §10.1). */
export const TRACE = {
  minSecondsTrue: 45,
  minSecondsDeep: 180,
  minSecondsAnchor: 480,
  maxSpeedKmh: 8,
  maxAccuracyMeters: 35,
  teleportDistanceMeters: 400,
  teleportWindowSeconds: 30,
  maxSpeedQuarantineKmh: 25,
  lifetimeHours: 36,
  wickLifetimeHours: 60,
  gpsRetentionDays: 14,
  /** Частота сэмпла GPS в фоне, сек (§10.1). */
  backgroundSampleSeconds: 10,
  /** Частота сэмпла на экране карты, сек (§10.1). */
  foregroundSampleSeconds: 5,
  /** Интервал отправки пакета на сервер, сек (§10.1). */
  batchIntervalSeconds: 30,
  /** Локальный буфер при офлайне, точек (§10.1). */
  offlineBufferMax: 50,
} as const;

/** Классификация качества по времени в соте (секунды пешком). */
export function classifyTraceQuality(secondsInHex: number): TraceQuality | null {
  if (secondsInHex < TRACE.minSecondsTrue) return null;
  if (secondsInHex >= TRACE.minSecondsAnchor) return "anchor";
  if (secondsInHex >= TRACE.minSecondsDeep) return "deep";
  return "true";
}

/** Сравнение качества: anchor > deep > true > echo. */
export function traceQualityRank(quality: TraceQuality): number {
  switch (quality) {
    case "echo":
      return 0;
    case "true":
      return 1;
    case "deep":
      return 2;
    case "anchor":
      return 3;
  }
}

/** Расстояние между двумя точками WGS84 в метрах. */
export function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadius = 6_371_000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(a));
}

/** Скорость между двумя точками, км/ч. */
export function speedKmh(
  lat1: number,
  lng1: number,
  t1Ms: number,
  lat2: number,
  lng2: number,
  t2Ms: number,
): number {
  const dtHours = Math.abs(t2Ms - t1Ms) / 3_600_000;
  if (dtHours === 0) return 0;
  return haversineMeters(lat1, lng1, lat2, lng2) / 1000 / dtHours;
}

/** Точка подозрительна для карантина (§10.1). */
export function isGpsQuarantine(input: {
  distanceMeters: number;
  deltaSeconds: number;
  speedKmh: number;
}): boolean {
  if (input.speedKmh > TRACE.maxSpeedQuarantineKmh) return true;
  if (
    input.distanceMeters > TRACE.teleportDistanceMeters &&
    input.deltaSeconds < TRACE.teleportWindowSeconds
  ) {
    return true;
  }
  return false;
}
